/**
 * Pure, dependency-free helpers for the partner-onboarding wizard's progress
 * model (FOO-6351 C0). Kept separate from `useOnboardingWizard` so the core
 * mapping/completion/resumption logic is exhaustively unit-testable without
 * pulling the React/api-client module graph.
 */

/**
 * Flatten the `GET /dashboard/onboarding-steps` response (an array of step
 * groups, each with `is_complete` + `children`) into a Set of every completed
 * step-key string — both group ids (`open_times_and_location`) and child ids
 * (`open_times_and_location#location`). This is the single source of truth for
 * phase completion.
 *
 * @param {Array<{id: string, is_complete: boolean, children?: Array<{id: string, is_complete: boolean}>}>} groups
 * @returns {Set<string>}
 */
export function collectCompletedStepKeys(groups) {
  const completed = new Set();

  (groups ?? []).forEach((group) => {
    if (group?.is_complete && group?.id) {
      completed.add(group.id);
    }

    (group?.children ?? []).forEach((child) => {
      if (child?.is_complete && child?.id) {
        completed.add(child.id);
      }
    });
  });

  return completed;
}

/**
 * Whether a phase counts as complete given the set of completed step keys and
 * the external-completion map (for phases whose completion is not an onboarding
 * step, e.g. KYC gateway verification).
 */
export function isPhaseComplete(phase, completedStepKeys, externalCompletion = {}) {
  if (phase.derivesCompletionExternally) {
    return Boolean(externalCompletion[phase.key]);
  }

  return Boolean(phase.completionStepKey) && completedStepKeys.has(phase.completionStepKey);
}

/**
 * The key of the first phase that is not yet complete — the phase a resumed
 * "Continue Setup" session deep-links to. Falls back to the last phase when
 * everything is already done.
 */
export function computeFirstIncompletePhaseKey(phases) {
  const firstIncomplete = phases.find((phase) => !phase.isComplete);

  return (firstIncomplete ?? phases[phases.length - 1]).key;
}
